import  { useRef, useState } from "react";
import { FaUser } from "react-icons/fa";
import { IoCloseOutline } from "react-icons/io5";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import api from "../../../utils/redux/api";
import { toast } from "react-toastify";
import axiosServicesOnboarding from "../../../services/axiosServicesOnboarding";
import { Box, Typography } from "@mui/material";

const StepBox = ({ number, title }) => {
  return (
    <Box position="relative">
      <Box
        display="flex"
        alignItems="center"
        border="1px solid #e0e0e0"
        borderRadius="8px"
        padding="6px"
        bgcolor="#ffffff"
        position="relative"
      >
        {/* Number Circle */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="40px"
          height="40px"
          border="2px solid #001d7e"
          borderRadius="50%"
          marginRight="8px"
        >
          <Typography variant="body1" color="#001d7e" fontWeight="bold">
            {number}
          </Typography>
        </Box>

        {/* Title */}
        <Typography variant="body1" fontWeight="bold" color="#001d7e">
          {title}
        </Typography>

        {/* Pointed edge - border */}
        <Box
          position="absolute"
          right="-21px"
          top="50%"
          sx={{
            transform: "translateY(-50%)",
            borderLeft: "21px solid #e0e0e0",
            borderTop: "21px solid transparent",
            borderBottom: "21px solid transparent",
          }}
        />

        {/* Pointed edge - fill */}
        <Box
          position="absolute"
          right="-20px"
          top="50%"
          sx={{
            transform: "translateY(-50%)",
            borderLeft: "20px solid #ffffff",
            borderTop: "20px solid transparent",
            borderBottom: "20px solid transparent",
            zIndex: 1,
            height: "60px",
          }}
        />
      </Box>
    </Box>
  );
};

const BvnVerification = ({ onBack, refresh, setRefresh }) => {
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const uniqueNumbersRef = useRef(null); // To persist unique numbers
  const [reSendOtpDetails, setReSendOtpDetails] = useState(null);
  const [reSendOtpLoading, setReSendOtpLoading] = useState(false);

  // Generate unique numbers once
  const generateUniqueNumbers = () => {
    if (!uniqueNumbersRef.current) {
      const uniqueSet = new Set();
      while (uniqueSet.size < 10) {
        const timestamp = Date.now(); // Current timestamp in milliseconds
        const randomNum = Math.floor(1000 + Math.random() * 9000); // 4-digit random number
        uniqueSet.add(`${timestamp}${randomNum}`);
      }
      uniqueNumbersRef.current = Array.from(uniqueSet);
    }
    return uniqueNumbersRef.current;
  };

  const validationSchema = Yup.object({
    bvn: Yup.string()
      .matches(/^[0-9]{10,15}$/, "Invalid bvn number")
      .required("bvn number is required"),
    otp: otpSent
      ? Yup.string()
          .required("OTP is required")
          .length(6, "OTP must be exactly 6 digits")
      : Yup.string(),
  });

  const handleResendOtp = async () => {
    try {
      setReSendOtpLoading(true);
      setError("");
      
      // Generate a new requestId each time
      const timestamp = Date.now();
      const randomNum = Math.floor(1000 + Math.random() * 9000);
      const newRequestId = `${timestamp}${randomNum}`;
      
      const payload = {
        bvn: reSendOtpDetails.bvn,
        requestId: newRequestId,
      };

      // Send OTP

      const res = await axiosServicesOnboarding.post(
        "Customer/generate-otp",
        payload
      );
      console.log("OTP sent response:", res);
      setOtpSent(true);
      toast.success(res?.data?.message || "OTP sent successfully!");
      
      // Update reSendOtpDetails with the new requestId for future resends
      setReSendOtpDetails({
        bvn: reSendOtpDetails.bvn,
        requestId: newRequestId,
      });
    } catch (error) {
      console.error("API error:", error);
      setError(
        error?.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setReSendOtpLoading(false);
    }
  };

  return (
    <div className="w-full md:w-[650px] mx-auto p-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2 text-gray-700 text-lg font-semibold">
          <FaUser className="text-xl text-primary-color" />
          <span>BVN Validation</span>
        </div>
        <button onClick={onBack} className="text-gray-600 text-2xl">
          {/* <IoCloseOutline /> */}
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.0003 29.3337C23.3641 29.3337 29.3337 23.3641 29.3337 16.0003C29.3337 8.63653 23.3641 2.66699 16.0003 2.66699C8.63653 2.66699 2.66699 8.63653 2.66699 16.0003C2.66699 23.3641 8.63653 29.3337 16.0003 29.3337Z"
              stroke="#25233A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M20 12L12 20"
              stroke="#25233A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 12L20 20"
              stroke="#25233A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="px-8 py-2 bg-[#edf1f9] rounded-md mb-8">
        <Box maxWidth="600px" width="100%" p={2}>
          <StepBox number="04" title="BVN Validation" />
        </Box>
      </div>

      <h3 className="text-lg font-semibold text-primary-color text-center mb-6">
        BVN Validation
      </h3>

      <Formik
        initialValues={{ bvn: "", otp: "" }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          const uniqueNumbers = generateUniqueNumbers(10); // Generate one unique number

          const body = {
            bvn: values.bvn,
            requestId: uniqueNumbers[0],
          };

          setReSendOtpDetails({
            bvn: values.bvn,
            requestId: uniqueNumbers[0],
          });

          try {
            setLoading(true);
            setError("");

            if (!otpSent) {
              // Send OTP
              const res = await axiosServicesOnboarding.post(
                "Customer/verify-bvn",
                body
              );
              console.log("OTP sent response:", res);
              setOtpSent(true);
              toast.success(res?.data?.message || "OTP sent successfully!");
            } else {
              // Validate OTP - use the most recent requestId from reSendOtpDetails
              const currentRequestId = reSendOtpDetails?.requestId || uniqueNumbers[0];
              const res = await axiosServicesOnboarding.post("customer/validate-otp", {
                otp: values.otp,
                requestId: currentRequestId,
              });
              console.log("OTP validation response:", res);
              // alert("Email verified successfully!");
              toast.success(res?.data?.message || "bvn verified successfully!");
              setRefresh(!refresh); // Refresh the parent component
              onBack();
            }
          } catch (error) {
            console.error("API error:", error?.response?.data?.message);
            toast.error(
              error?.response?.data?.message ||
                "Something went wrong. Please try again."
            );
          } finally {
            setLoading(false);
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form className="space-y-6 w-full md:w-[400px] mx-auto">
            <div>
              <label className="block text-gray-600 font-medium mb-2">
                Input BVN <span className="text-red-500">*</span>
              </label>
              <Field
                type="text"
                name="bvn"
                placeholder="Enter your bvn number"
                className="w-full p-3 border border-gray-300 rounded-md text-cente"
                disabled={otpSent}
              />
              <ErrorMessage
                name="bvn"
                component="div"
                className="text-red-500 text-sm mt-1"
              />
            </div>

            {!otpSent && (
              <div className="text-[13px] text-[#171832] text-center w-full mx-auto flex items-center justify-center">
                <p className="w-[285px] text-[#171832]">
                  We will check if this BVN matches personal information (first
                  and last name)
                </p>
              </div>
            )}

            {otpSent && (
              <div>
                <label className="block text-gray-600 font-medium mb-2">
                  Enter OTP:
                </label>
                <Field
                  type="text"
                  name="otp"
                  placeholder="Enter OTP"
                  className="w-full p-3 border border-gray-300 rounded-md text-cente"
                />
                <ErrorMessage
                  name="otp"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>
            )}

            {otpSent ? (
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary-color hover:bg-[#8388B6] text-white font-semibold py-4 px-6 rounded-md"
                >
                {loading ? "Validating..." : "Validate"}
              </button>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary-color hover:bg-[#8388B6] text-white font-semibold py-4 px-6 rounded-md"
                >
                {loading ? "Validating..." : "Validate"}
              </button>
            )}

            {otpSent && (
              <p onClick={() => handleResendOtp()} className="text-sm text-gray-500 mt-2 text-center">
                Didn’t get OTP?{" "}
                <span className="text-primary-color cursor-pointer">
                  {reSendOtpLoading ? "Resending..." : "Resend OTP"}
                </span>
              </p>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default BvnVerification;
